import React from 'react';
import {
  Theme,
  WithStyles,
  createStyles,
  withStyles,
} from '@lifeomic/chroma-react/styles';
import isEmpty from 'lodash/isEmpty';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { SurveyItemComponent } from './SurveyItem';
import SurveyItem from '../util/SurveyItem';
import {
  withSurveyContext,
  withSurveyResponseContext,
  withIsReadOnly,
  WithSurveyContextInjectedProps,
  WithSurveyResponseContextInjectedProps,
  WithIsReadOnlyInjectedProps,
} from './Contexts';
import {
  defineMessages,
  FormattedMessage,
  injectIntl,
  WrappedComponentProps,
} from 'react-intl';
import { Formik, FormikHelpers } from 'formik';
import SubmitButton, { DialogText } from './SubmitButton';
import { getTestProps } from '../../../util/testProps';
import { ChevronLeft, ChevronRight } from '@lifeomic/chromicons';
import SurveyLayout from './SurveyLayout';
import clsx from 'clsx';
import { SurveyOverrideClasses } from './SurveyOverrideClasses';
import ErrorScrollWrapper from './ErrorScrollWrapper';
import withIsMobile, {
  WithIsMobileInjectedProps,
} from '../../../hoc/withIsMobile';
import SaveProgressButton from './SaveProgressButton';
import { get } from 'lodash';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { Fade } from '@mui/material';
import withResponseSubject, {
  WithResponseSubjectInjectedProps,
} from './hoc/withResponseSubject';
import getFormMeta from '../util/getFormMeta';

export const testIds = {
  surveyPreviousPageButton: 'survey-previousPage-button',
  surveyDraftButton: 'survey-draft-button',
  surveyNextPageButton: 'survey-nextPage-button',
};

const messages = defineMessages({
  buttonText: {
    id: 'surveys.submit.buttonText',
    defaultMessage: 'Submit and continue',
  },
  cancel: {
    id: 'surveys.submit.cancel',
    defaultMessage: 'Back to survey',
  },
  confirm: {
    id: 'surveys.submit.confirm',
    defaultMessage: 'Yes, submit',
  },
  dialogHeader: {
    id: 'surveys.submit.dialogHeader',
    defaultMessage: 'Submit and continue?',
  },
  dialogContent: {
    id: 'surveys.submit.dialogContent',
    defaultMessage:
      'You’re about to submit this section of responses and move on to the next section. Once you submit a section, you cannot go back and edit those responses.',
  },
});

const styles = (theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
      overflowY: 'auto',
    },
    paginationControlContainer: {
      width: '100%',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: theme.spacing(3),
    },
    itemsContainer: {
      width: '100%',
    },
    paper: {
      background: theme.palette.common.white,
      marginTop: theme.spacing(4),
      padding: theme.spacing(4),
      overflowX: 'auto',
    },
    headerTextContainer: {
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
    form: {
      width: '100%',
      height: '100%',
    },
    formLine: {
      width: '100%',
      display: 'flex',
      // bottom-align so inputs stay on one line when a label wraps
      alignItems: 'flex-end',
      gap: theme.spacing(2),
    },
    // Mobile stacking is CSS-only: the line wrappers always render so the
    // element tree keeps the same shape across the breakpoint. Otherwise
    // resizing across it would remount every child and wipe local UI state
    // (e.g. collapsible section expand/collapse).
    formLineStacked: {
      flexDirection: 'column',
      alignItems: 'stretch',
    },
    formLineItem: {
      minWidth: 0,
      flex: 1,
    },
    rightButtonsContainer: {
      display: 'flex',
      justifyContent: 'space-between',
    },
  });

interface PaginatedSurveyFormOwnProps {
  onNextPage: () => void;
  hasNextPage: boolean;
  onPrevPage: () => void;
  hasPrevPage: boolean;
  surveyItem: SurveyItem;
  onCompletion: () => void;
  onSaveProgress: () => void;
  dialogText: DialogText;
  prevPageDisabled: boolean;
  enforcePageCheckpoint: boolean;
  overrideClasses: SurveyOverrideClasses;
  surveyInReadOnlyMode: boolean;
  onSaveDraft?: (questionnaireResponse: fhir.QuestionnaireResponse) => void;
  autosaveTimestamp?: string;
  hideSaveProgressButton?: boolean;
  /**
   * Bumped when a repeating-group instance is added or removed. Formik
   * reseeds from the current response without remounting this form.
   */
  formikReseedRevision?: number;
}

interface PaginatedSurveyFormState {
  formikReseedRevision: number;
  initialValues: Record<string, any>;
}

type PaginatedSurveyProps = PaginatedSurveyFormOwnProps &
  WithStyles<typeof styles> &
  WithSurveyContextInjectedProps &
  WithIsReadOnlyInjectedProps &
  WithSurveyResponseContextInjectedProps &
  WrappedComponentProps &
  WithIsMobileInjectedProps &
  WithResponseSubjectInjectedProps;

class PaginatedSurveyForm extends React.PureComponent<
  PaginatedSurveyProps,
  PaginatedSurveyFormState
> {
  private checkpointDialogText: DialogText;
  private submittedOn: string;
  private completedBy: string;
  private isComplete: boolean;

  static getDerivedStateFromProps(
    props: PaginatedSurveyProps,
    state: PaginatedSurveyFormState,
  ): Partial<PaginatedSurveyFormState> | null {
    const nextRevision = props.formikReseedRevision ?? 0;
    if (nextRevision === state.formikReseedRevision) {
      return null;
    }
    const survey = props.survey;
    if (!survey) {
      return { formikReseedRevision: nextRevision, initialValues: {} };
    }
    return {
      formikReseedRevision: nextRevision,
      initialValues: survey.getInitialValues(
        props.surveyResponse,
        props.surveyItem.link,
      ),
    };
  }

  constructor(props: PaginatedSurveyProps) {
    super(props);

    const formatMessage = props.intl.formatMessage;
    // dialog text should not change during the lifecycle of this sub-form.
    // Formik initial values stay put except when formikReseedRevision bumps
    // (repeating-group add/remove); enableReinitialize then reseeds in place.
    this.state = {
      formikReseedRevision: props.formikReseedRevision ?? 0,
      initialValues:
        props.survey?.getInitialValues(
          props.surveyResponse,
          props.surveyItem.link,
        ) ?? {},
    };
    this.checkpointDialogText = {
      cancelButtonText: formatMessage(messages.cancel),
      confirmButtonText: formatMessage(messages.confirm),
      dialogHeader: formatMessage(messages.dialogHeader),
      dialogContent: formatMessage(messages.dialogContent),
      buttonText: formatMessage(messages.buttonText),
    };

    const { submittedOnMessage, completedByMessage } = getFormMeta(
      props.intl,
      props.surveyResponse?.authoredDate,
      props.patient,
    );

    this.submittedOn = submittedOnMessage;
    this.completedBy = completedByMessage;
    this.isComplete = props.surveyResponse.isComplete;
  }

  handleSubmit = (
    values: Record<string, any>,
    { setSubmitting }: FormikHelpers<Record<string, any>>,
  ) => {
    const buttonClicked = get(values, 'buttonClicked');

    if (buttonClicked === 'save') {
      this.props.onSaveProgress();
    } else if (this.props.hasNextPage) {
      this.props.onNextPage();
    } else if (buttonClicked === 'complete') {
      this.props.onCompletion();
    }

    setSubmitting(false);
  };

  render() {
    const {
      classes,
      hasNextPage,
      onPrevPage,
      hasPrevPage,
      surveyItem,
      isReadOnly,
      dialogText,
      prevPageDisabled,
      enforcePageCheckpoint,
      overrideClasses,
      onSaveDraft,
      surveyResponse,
      isMobile,
      autosaveTimestamp,
      hideSaveProgressButton,
    } = this.props;

    // next page navigation should not prompt for confirmation
    const allowUnpromptedNextNavigation =
      (hasNextPage || isReadOnly) && !enforcePageCheckpoint;

    const confirmStripItems = surveyItem.subItems.filter(
      item => item.isConfirmStrip,
    );
    const bodyLines = surveyItem.subItemsGroupedByDisplayLines.filter(
      lineSurveyItems => !lineSurveyItems.some(item => item.isConfirmStrip),
    );

    return (
      <div className={clsx(classes.root, overrideClasses.formRoot)}>
        <Formik
          enableReinitialize
          initialValues={this.state.initialValues}
          onSubmit={this.handleSubmit}
        >
          {({
            handleSubmit,
            submitForm,
            setFieldValue,
            errors,
            isSubmitting,
            dirty,
          }) => (
            <form
              id="survey-form"
              onSubmit={handleSubmit}
              className={clsx(classes.form, overrideClasses.form)}
            >
              <ErrorScrollWrapper errors={errors} isSubmitting={isSubmitting}>
                <SurveyLayout
                  surveyItem={surveyItem}
                  pinnedContent={
                    confirmStripItems.length > 0 ? (
                      <>
                        {confirmStripItems.map(item => (
                          <SurveyItemComponent
                            key={item.link}
                            surveyItem={item}
                          />
                        ))}
                      </>
                    ) : undefined
                  }
                  surveyContent={
                    <>
                      {/* Lines always render (stacked via CSS on mobile) so
                          crossing the breakpoint never remounts children. */}
                      {bodyLines.map((lineSurveyItems, index) => (
                        <div
                          className={clsx(
                            classes.formLine,
                            isMobile && classes.formLineStacked,
                          )}
                          key={lineSurveyItems[0]?.link ?? index}
                        >
                          {lineSurveyItems.map(lineSurveyItem => (
                            <SurveyItemComponent
                              key={lineSurveyItem.link}
                              surveyItem={lineSurveyItem}
                            />
                          ))}
                        </div>
                      ))}
                    </>
                  }
                  leftButton={
                    hasPrevPage ? (
                      <Button
                        disabled={!isEmpty(errors) || prevPageDisabled}
                        onClick={onPrevPage}
                        variant="outlined"
                        className={clsx(overrideClasses.previousPageButton)}
                        icon={ChevronLeft}
                        {...getTestProps(testIds.surveyPreviousPageButton)}
                      >
                        <FormattedMessage
                          id="surveys.paginatedSurvey.prevPage"
                          defaultMessage="Previous"
                        />
                      </Button>
                    ) : undefined
                  }
                  draftButton={
                    <Box direction="column">
                      {autosaveTimestamp && !isReadOnly && (
                        <Fade key={autosaveTimestamp} appear in timeout={1000}>
                          <Text size="caption">
                            <FormattedMessage
                              id="surveys.submit.autosavedMessage"
                              defaultMessage="Autosaved at {autosaveTimestamp}"
                              values={{ autosaveTimestamp }}
                            />
                          </Text>
                        </Fade>
                      )}
                      {onSaveDraft ? (
                        <Button
                          disabled={isReadOnly || !dirty}
                          onClick={() =>
                            onSaveDraft(
                              surveyResponse.toFhirQuestionnaireResponse(),
                            )
                          }
                          type="button"
                          variant="contained"
                          className={clsx(overrideClasses.surveyDraftButton)}
                          {...getTestProps(testIds.surveyDraftButton)}
                        >
                          <FormattedMessage
                            id="surveys.paginatedSurvey.draft"
                            defaultMessage="Save Draft"
                          />
                        </Button>
                      ) : null}
                    </Box>
                  }
                  rightButton={
                    <div className="rightButtonsContainer">
                      {this.isComplete && (
                        <>
                          <Text>{this.submittedOn}</Text>
                          <Text>{this.completedBy}</Text>
                        </>
                      )}
                      {!isReadOnly && !hideSaveProgressButton && (
                        <SaveProgressButton
                          overrideClasses={overrideClasses}
                          dialogText={
                            enforcePageCheckpoint
                              ? this.checkpointDialogText
                              : dialogText
                          }
                          submitIncompleteForm={() => {
                            setFieldValue('buttonClicked', 'save');
                            submitForm();
                          }}
                        />
                      )}
                      {allowUnpromptedNextNavigation ? (
                        <Button
                          form="survey-form"
                          disabled={!hasNextPage}
                          variant="contained"
                          type="submit"
                          className={clsx(overrideClasses.nextPageButton)}
                          trailingIcon={ChevronRight}
                          {...getTestProps(testIds.surveyNextPageButton)}
                        >
                          <FormattedMessage
                            id="surveys.paginatedSurvey.nextPage"
                            defaultMessage="Next"
                          />
                        </Button>
                      ) : (
                        <SubmitButton
                          overrideClasses={overrideClasses}
                          submitForm={() => {
                            setFieldValue('buttonClicked', 'complete');
                            submitForm();
                          }}
                          dialogText={
                            enforcePageCheckpoint
                              ? this.checkpointDialogText
                              : dialogText
                          }
                        />
                      )}
                    </div>
                  }
                />
              </ErrorScrollWrapper>
            </form>
          )}
        </Formik>
      </div>
    );
  }
}

export default withStyles(styles)(
  withSurveyContext(
    withSurveyResponseContext(
      withIsReadOnly(
        withResponseSubject(withIsMobile(injectIntl(PaginatedSurveyForm))),
      ),
    ),
  ),
);
